import React, { useEffect, useState } from "react";
import axios from "axios";
import { FaEdit, FaTrash, FaChevronLeft, FaChevronRight } from "react-icons/fa";
import { BiSortAlt2 } from "react-icons/bi";
import { MdSearch } from "react-icons/md";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import ReactPaginate from "react-paginate";

const TablePosts = ({ selectedCategory }) => {
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [showDeletePrompt, setShowDeletePrompt] = useState(false);
  const [deletePostsId, setDeletePostsId] = useState(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [sortOrder, setSortOrder] = useState("desc");
  const postsPerPage = 10;

  // Fungsi untuk fetch data dari server tanpa cache
  const fetchPostsData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`/api_w/posts`, {
        headers: { 
          db_name: process.env.REACT_APP_DB_NAME,
          "Cache-Control": "no-cache, no-store, must-revalidate",
          "Pragma": "no-cache",
          "Expires": "0"
        },
        params: {
          timestamp: new Date().getTime() // Force refresh
        }
      });
      const sorted = (response.data.data || []).sort((a, b) => new Date(b.date) - new Date(a.date));
      setPosts(sorted);
      setCurrentPage(0); // Reset ke halaman pertama
    } catch (error) {
      toast.error("Error fetching posts data: " + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  // Fetch data saat component mount
  useEffect(() => {
    fetchPostsData();
  }, []);

  const formatDate = (dateString) => {
    const options = { year: "numeric", month: "long", day: "numeric" };
    return new Date(dateString).toLocaleDateString("id-ID", options);
  };

  const handleConfirmDelete = async () => {
    try {
      await axios.delete(`/api_w/posts/${deletePostsId}`, {
        headers: {
          Authorization: localStorage.getItem("token"),
          db_name: process.env.REACT_APP_DB_NAME,
          "Cache-Control": "no-cache, no-store, must-revalidate",
          "Pragma": "no-cache",
          "Expires": "0"
        }
      });
      toast.success("Berhasil Hapus Postingan!");
      
      // Hapus dari state lokal
      setPosts((prev) => prev.filter((p) => p.id !== deletePostsId));
      setCurrentPage(0);
      
    } catch (error) {
      toast.error("Error Hapus Postingan: " + (error.response?.data?.message || error.message));
    } finally {
      setShowDeletePrompt(false);
      setDeletePostsId(null);
    }
  };

  const handleSortByDate = () => {
    const sortedPosts = [...posts].sort((a, b) => {
      const dateA = new Date(a.date);
      const dateB = new Date(b.date);
      return sortOrder === "asc" ? dateA - dateB : dateB - dateA;
    });
    setPosts(sortedPosts);
    setSortOrder(sortOrder === "asc" ? "desc" : "asc");
  };

  const filteredPosts = posts
    .filter((post) => !selectedCategory || post.category_id === parseInt(selectedCategory))
    .filter((post) => !searchQuery || post.title?.toLowerCase().includes(searchQuery.toLowerCase()));

  const pageCount = Math.ceil(filteredPosts.length / postsPerPage);
  const offset = currentPage * postsPerPage;
  const currentPosts = filteredPosts.slice(offset, offset + postsPerPage);

  const handlePageClick = ({ selected }) => setCurrentPage(selected);

  if (loading) {
    return (
      <div className="p-8 text-center">
        <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-brand-500 border-r-transparent" />
        <p className="mt-3 text-sm text-gray-500">Memuat data...</p>
      </div>
    );
  }

  return (
    <div>
      {/* Search & Count Bar */}
      <div className="flex items-center justify-between gap-4 border-b border-gray-100 px-5 py-4">
        <div className="relative flex-1 max-w-xs">
          <MdSearch className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            placeholder="Cari judul postingan..."
            value={searchQuery}
            onChange={(e) => { setSearchQuery(e.target.value); setCurrentPage(0); }}
            className="w-full rounded-xl border border-gray-200 bg-gray-50 pl-9 pr-3 py-2 text-sm text-gray-700 outline-none focus:border-brand-500 focus:ring-1 focus:ring-brand-500"
          />
        </div>
        <span className="text-sm text-gray-400">{filteredPosts.length} postingan</span>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full text-left">
          <thead>
            <tr className="border-b border-gray-100 bg-gray-50">
              <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wider text-gray-400">No</th>
              <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wider text-gray-400">Gambar</th>
              <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wider text-gray-400">Judul & Kategori</th>
              <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wider text-gray-400">Author</th>
              <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wider text-gray-400">
                <button onClick={handleSortByDate} className="flex items-center gap-1 hover:text-gray-600">
                  Tanggal <BiSortAlt2 className="h-4 w-4" />
                </button>
              </th>
              <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wider text-gray-400">Status</th>
              <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wider text-gray-400 text-center">Aksi</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {currentPosts.length === 0 ? (
              <tr><td colSpan={7} className="py-12 text-center text-sm text-gray-400">Tidak ada postingan ditemukan</td></tr>
            ) : currentPosts.map((item, index) => (
              <tr key={item.id} className="hover:bg-gray-50/80 transition-colors">
                <td className="px-5 py-4 text-sm text-gray-500">{offset + index + 1}</td>
                <td className="px-5 py-4">
                  <div className="h-12 w-16 overflow-hidden rounded-lg">
                    <img src={item.image} alt={item.title} className="h-full w-full object-cover" />
                  </div>
                </td>
                <td className="px-5 py-4">
                  <p className="text-sm font-semibold text-navy-700 line-clamp-1">{item.title}</p>
                  <p className="text-xs text-gray-400 mt-0.5">{item.PostCategory?.name}</p>
                </td>
                <td className="px-5 py-4 text-sm text-gray-600">{item.User?.name}</td>
                <td className="px-5 py-4 text-sm text-gray-500 whitespace-nowrap">{formatDate(item.date)}</td>
                <td className="px-5 py-4">
                  <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
                    item.status ? "bg-green-50 text-green-700" : "bg-red-50 text-red-600"
                  }`}>
                    {item.status ? "Aktif" : "Non-Aktif"}
                  </span>
                </td>
                <td className="px-5 py-4">
                  <div className="flex items-center justify-center gap-2">
                    <button
                      onClick={() => navigate(`/admin/edit-posts/${item.id}`)}
                      className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-50 text-blue-500 hover:bg-blue-100 transition-colors"
                      title="Edit"
                    >
                      <FaEdit className="h-3.5 w-3.5" />
                    </button>
                    <button
                      onClick={() => { setDeletePostsId(item.id); setShowDeletePrompt(true); }}
                      className="flex h-8 w-8 items-center justify-center rounded-lg bg-red-50 text-red-500 hover:bg-red-100 transition-colors"
                      title="Hapus"
                    >
                      <FaTrash className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {pageCount > 1 && (
        <div className="flex items-center justify-between border-t border-gray-100 px-5 py-4">
          <span className="text-sm text-gray-400">Halaman {currentPage + 1} dari {pageCount}</span>
          <ReactPaginate
            previousLabel={<FaChevronLeft className="h-3 w-3" />}
            nextLabel={<FaChevronRight className="h-3 w-3" />}
            breakLabel={"..."}
            pageCount={pageCount}
            marginPagesDisplayed={1}
            pageRangeDisplayed={3}
            onPageChange={handlePageClick}
            forcePage={currentPage}
            containerClassName={"flex items-center gap-1"}
            pageClassName={""}
            pageLinkClassName={"flex h-8 w-8 items-center justify-center rounded-lg text-sm text-gray-600 hover:bg-gray-100"}
            activeClassName={""}
            activeLinkClassName={"!bg-brand-500 !text-white font-semibold"}
            previousClassName={""}
            previousLinkClassName={"flex h-8 w-8 items-center justify-center rounded-lg text-gray-400 hover:bg-gray-100"}
            nextClassName={""}
            nextLinkClassName={"flex h-8 w-8 items-center justify-center rounded-lg text-gray-400 hover:bg-gray-100"}
            disabledLinkClassName={"opacity-40 cursor-not-allowed"}
          />
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeletePrompt && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="mx-4 w-full max-w-sm rounded-2xl bg-white p-6 shadow-2xl">
            <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-red-50">
              <FaTrash className="h-5 w-5 text-red-500" />
            </div>
            <h3 className="mb-2 text-lg font-bold text-navy-700">Hapus Postingan?</h3>
            <p className="mb-6 text-sm text-gray-500">Tindakan ini tidak dapat dibatalkan. Postingan akan dihapus secara permanen.</p>
            <div className="flex gap-3">
              <button onClick={() => setShowDeletePrompt(false)} className="flex-1 rounded-xl border border-gray-200 py-2.5 text-sm font-medium text-gray-600 hover:bg-gray-50 transition-colors">
                Batal
              </button>
              <button onClick={handleConfirmDelete} className="flex-1 rounded-xl bg-red-500 py-2.5 text-sm font-bold text-white hover:bg-red-600 transition-colors">
                Hapus
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TablePosts;